import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from 'services/api';
import { error, log } from 'utils/logger';
import { joinUrl } from 'lib/utils';

interface BitbucketContextType {
  bitbucketConnected: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  isLoading: boolean;
  checkBitbucketAuth: () => Promise<void>;
  setBitbucketConnected: (connected: boolean) => void;
  setIsConnecting: (connecting: boolean) => void;
  setIsDisconnecting: (disconnecting: boolean) => void;
}

const BitbucketContext = createContext<BitbucketContextType | undefined>(undefined);

export const BitbucketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [bitbucketConnected, setBitbucketConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkBitbucketAuth = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders()
      if (!headers) {
        error('Auth token not found');
        setBitbucketConnected(false);
        setIsLoading(false);
        return;
      }

      log('Checking Bitbucket auth status...');
      const response = await axios.get(joinUrl(process.env.REACT_APP_MIDDLEWARE_BACKEND_URL || '', 'bitbucket/check-auth'), {
        headers,
        withCredentials: true
      });

      const data = response.data;
      log('Bitbucket auth check response:', data);
      
      setBitbucketConnected(data.message === "Authenticated");
    } catch (err : any) {
      error('Error checking Bitbucket auth:', err);
      setBitbucketConnected(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only check auth if we have auth headers
    const headers = getAuthHeaders();
    if (headers) {
      checkBitbucketAuth();
    } else {
      setIsLoading(false);
      setBitbucketConnected(false);
    }
  }, [checkBitbucketAuth]);

  return (
    <BitbucketContext.Provider value={{
      bitbucketConnected,
      isConnecting,
      isDisconnecting,
      isLoading,
      checkBitbucketAuth,
      setBitbucketConnected,
      setIsConnecting,
      setIsDisconnecting
    }}>
      {children}
    </BitbucketContext.Provider>
  );
};

export const useBitbucket = () => {
  const context = useContext(BitbucketContext);
  if (context === undefined) {
    throw new Error('useBitbucket must be used within a BitbucketProvider');
  }
  return context;
};
